/* eslint-disable react-refresh/only-export-components */
import { useProfile } from '@/hooks/useProfile';
import type { AdminType } from '@gokrew/models/admins';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import type { LoginApiResponse, LoginForm } from '@gokrew/models/auth';
import { login, logoutUser } from '@/api/auth';
import { tokenManager, AUTH_SESSION_KEY } from '@/auth';
import { decodeAccessTokenClaims } from '@gokrew/ui/auth';
import i18n from '@/i18n';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: AdminType | 'crm_user';
  passwordChangedAt: string | null;
  temporaryPasswordGeneratedAt: string | null;
}

export interface AuthContextType {
  user: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  profileError: boolean;
  login: (payload: LoginForm) => Promise<LoginApiResponse>;
  commitAccessToken: (accessToken: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Register session-expired callback — redirect to login
  useEffect(() => {
    tokenManager.onSessionExpired(() => {
      setUser(null);
      setToken(null);
      localStorage.removeItem(AUTH_SESSION_KEY);
      window.location.href = '/login';
    });
  }, []);

  // Silently restore session on mount (page refresh / new tab)
  useEffect(() => {
    async function restoreSession() {
      try {
        if (!localStorage.getItem(AUTH_SESSION_KEY)) {
          setToken(null);
          return;
        }

        const restored = await tokenManager.getValidToken();
        setToken(restored);
      } finally {
        setIsInitializing(false);
      }
    }

    restoreSession();
  }, []);

  // -------------------------
  // FETCH PROFILE
  // -------------------------
  const {
    data: profileData,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = useProfile({
    enabled: !!token,
  });

  const refreshUser = async () => {
    await refetchProfile();
  };

  useEffect(() => {
    if (profileData?.data) {
      const profile = profileData.data;

      setUser({
        id: profile.user.id,
        email: profile.user.email,
        name: `${profile.user.firstName} ${profile.user.lastName}`,
        role: profile.type,
        passwordChangedAt: profile.user.passwordChangedAt,
        temporaryPasswordGeneratedAt: profile.user.temporaryPasswordGeneratedAt,
      });
      const savedLanguage = profile.user.language || 'en';
      if (savedLanguage !== i18n.language) {
        i18n.changeLanguage(savedLanguage);
      }
    }
  }, [profileData]);

  // -------------------------
  // LOGIN
  // -------------------------
  const loginMutation = useMutation({
    mutationFn: (payload: LoginForm) => login(payload),
  });

  // sessionId/role are no longer echoed in the response body — they're
  // signed into the access token itself.
  const commitAccessToken = (accessToken: string) => {
    const { sessionId } = decodeAccessTokenClaims(accessToken);
    tokenManager.setToken(accessToken);
    setToken(accessToken);
    if (sessionId) localStorage.setItem(AUTH_SESSION_KEY, sessionId);
    setUser(null);
  };

  const loginUser = async (payload: LoginForm): Promise<LoginApiResponse> => {
    const response = await loginMutation.mutateAsync(payload);
    // 2FA disabled server-side (or already past it) — tokens come back
    // directly; commit immediately. Otherwise `{ sessionId }` means the
    // caller must show the OTP step and call commitAccessToken() itself
    // once /auth/verify-otp succeeds.
    if ('accessToken' in response.data) {
      commitAccessToken(response.data.accessToken);
    }
    return response;
  };

  // -------------------------
  // LOGOUT
  // -------------------------

  const logoutMutation = useMutation({
    mutationFn: logoutUser,
  });

  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (err) {
      console.error('Logout API failed', err);
    } finally {
      tokenManager.clearToken();
      setUser(null);
      setToken(null);
      localStorage.removeItem(AUTH_SESSION_KEY);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading: loginMutation.isPending || profileLoading,
        isInitializing,
        profileError,
        login: loginUser,
        commitAccessToken,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
